import React, { useEffect, useState } from 'react';
import { FIELD_NAME } from '../../../constants/form-constants';
import { useApp } from '../../../contexts/app-context';
import { vehicleEntity } from '../../../entities/vehicle-entity';
import { formService } from '../../../form-service';
import { getCarlines } from '../../../service/api/get-carlines';
import { getExternalContentSalesgroups } from '../../../service/api/get-salesgroups';
import { Carline, Salesgroup } from '../../../types/vehicle-types';
import { SelectExtended } from './core/select-extended';

interface Props {
	label?: string;
	required?: boolean;
	carlines?: Carline[];
	salesgroups?: Salesgroup[];
	carlineCode?: string | number;
}

/*
 * VehicleSelect
 *
 * Se cargan los salesgroups definidos en externalContent. Si no hay ningun salesgroup definido en externalContent se cargan los carlines (filtrados por externalContent).
 *
 */
export function VehicleSelect(props: Props) {
	const { label, carlines, salesgroups, carlineCode, ...selectProps } = props;
	const { setAppError, setAppErrorLog } = useApp();
	const [options, setOptions] = useState<{ text: string; value: any }[]>([]);
	const [availableCarlines, setAvailableCarlines] = useState<Carline[]>([]);
	const [availableSalesgroups, setAvailableSalesgroups] = useState<Salesgroup[]>();

	useEffect(() => {
		let cancelled = false;

		// Priorizamos la carga de Salesgroups
		if (salesgroups) mapppingSalesgroupOptions(salesgroups);
		if (carlines) mapppingCarlineOptions(carlines);
		if (!salesgroups && !carlines) {
			getExternalContentSalesgroups().then((externalSalesgroups) => {
				if (cancelled) return;
				if (externalSalesgroups) {
					mapppingSalesgroupOptions(externalSalesgroups);
					return;
				}
				getCarlines().then((loadedCarlines) => {
					if (cancelled) return;
					if (!loadedCarlines) {
						setAppError(true);
						setAppErrorLog('No se ha obtenido ningún carline.');
					} else {
						mapppingCarlineOptions(loadedCarlines);
					}
				});
			});
		}

		return () => {
			cancelled = true;
		};
	}, []);

	// Seleccionar cuando carlineCode cambia y tenemos carlines disponibles
	useEffect(() => {
		if (carlineCode && availableCarlines.length > 0) {
			const carline = availableCarlines.find((carline) => String(carline.code) === String(carlineCode));
			if (carline) {
				formService.setFieldValue(FIELD_NAME.CARLINE, carline);
			}
		}
	}, [carlineCode, availableCarlines]);

	const mapppingCarlineOptions = (carlines: Carline[]) => {
		// Mapeamos options <key:text> con carlines ordenamos alfabeticamente y añadiendo "Nuevo"
		const sortedCarlines = [...carlines].sort((a, b) => b.prioridad - a.prioridad || a.title.localeCompare(b.title));
		const mappedOptions = sortedCarlines.map((carline) => ({
			text: (carline.nuevo ? 'Nuevo ' : '') + carline.title,
			value: carline,
		}));
		setOptions(mappedOptions);
		setAvailableCarlines(sortedCarlines);
	};

	const mapppingSalesgroupOptions = (salesgroups: Salesgroup[]) => {
		// Mapeamos options: text = title, value = salesgroup (el formulario guarda el objeto); ordenamos alfabeticamente
		const sortedSalesgroups = [...salesgroups].sort((a, b) => a.title.localeCompare(b.title));
		const mappedOptions = sortedSalesgroups.map((sg) => ({
			text: sg.title,
			value: sg,
		}));
		setOptions(mappedOptions);
		setAvailableSalesgroups(sortedSalesgroups);
	};

	const effectiveCarlines = carlines ?? availableCarlines;
	const effectiveSalesgroups = salesgroups ?? availableSalesgroups;
	const fieldName = effectiveSalesgroups ? FIELD_NAME.SALESGROUP : FIELD_NAME.CARLINE;

	// Si solo hay una opción se preselecciona, sin pisar un valor ya existente
	useEffect(() => {
		if (effectiveSalesgroups?.length === 1 && !vehicleEntity.salesgroup) {
			formService.setFieldValue(FIELD_NAME.SALESGROUP, effectiveSalesgroups[0]);
		} else if (!effectiveSalesgroups && effectiveCarlines.length === 1 && !vehicleEntity.carline) {
			formService.setFieldValue(FIELD_NAME.CARLINE, effectiveCarlines[0]);
		}
	}, [effectiveCarlines, effectiveSalesgroups]);

	// Con una única opción no hay nada que elegir
	const disabled = effectiveSalesgroups ? effectiveSalesgroups.length === 1 : effectiveCarlines.length === 1;

	return (
		<SelectExtended
			{...selectProps}
			key={fieldName}
			disabled={disabled}
			name={fieldName}
			label={label ? label : 'Modelo'}
			options={options}
		/>
	);
}
